import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

// Create a context to hold the configuration data
const ConfigContext = createContext(null);

/**
 * This provider component fetches configuration from the backend /api/config
 * endpoint when the application first loads. It makes the config data
 * available to all child components.
 */
export const ConfigProvider = ({ children }) => {
    const [config, setConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // This function runs only once when the app loads
        const fetchConfig = async () => {
            try {
                const response = await api.get('/config');
                setConfig(response.data);
            } catch (error) {
                console.error("FATAL: Could not fetch app configuration from the server.", error);
                // In a real-world app, you might want to show a full-page error screen here
                // to indicate that the frontend cannot connect to the backend properly.
            } finally {
                setIsLoading(false);
            }
        };

        fetchConfig();
    }, []); // The empty dependency array [] ensures this effect runs only once.

    const value = { config, isLoading };

    return (
        <ConfigContext.Provider value={value}>
            {children}
        </ConfigContext.Provider>
    );
};

/**
 * A custom hook that allows any component to easily access the
 * configuration data fetched by ConfigProvider.
 */
export const useConfig = () => {
    const context = useContext(ConfigContext);
    if (context === undefined) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }
    return context;
};
